import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Newspaper, Download, Mail, Film, Copy, Check } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SoundToggle from "@/components/SoundToggle";
import { toast } from "sonner";

const FACT_ITEMS = (t) => [
  { k: "genre", v: t("press.fact_genre_v") },
  { k: "audience", v: t("press.fact_audience_v") },
  { k: "platforms", v: t("press.fact_platforms_v") },
  { k: "price", v: t("press.fact_price_v") },
  { k: "languages", v: t("press.fact_languages_v") },
  { k: "offline", v: t("press.fact_offline_v") },
  { k: "release", v: t("press.fact_release_v") },
  { k: "studio", v: "Emergent Labs" },
];

function CopyBox({ label, text }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(label ? `${label} copié` : "Copié");
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error("Impossible de copier");
    }
  };
  return (
    <div className="glass rounded-2xl p-4 relative">
      <button
        onClick={doCopy}
        className="absolute top-2 right-2 text-stone-400 hover:text-amber-300 p-1 rounded"
        data-testid={`copy-${label?.toLowerCase().replace(/\s+/g, "-") || "text"}`}
        aria-label="Copier"
      >
        {copied ? <Check className="w-4 h-4 text-emerald-300" /> : <Copy className="w-4 h-4" />}
      </button>
      {label && <div className="text-[10px] uppercase tracking-widest text-amber-300/80 font-bold mb-2">{label}</div>}
      <p className="text-sm text-stone-200 whitespace-pre-line leading-relaxed pr-6">{text}</p>
    </div>
  );
}

export default function Press() {
  const { t } = useTranslation();
  const trailerScript = t("press.trailer_body");
  const pressReleaseShort = t("press.release_short");
  const pressReleaseLong = t("press.release_long");

  return (
    <div className="relative min-h-screen hero-radial grain">
      <header className="relative z-30 flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/" className="flex items-center gap-2 text-stone-300 hover:text-amber-200" data-testid="back-home">
          <ChevronLeft className="w-4 h-4" /> {t("common.back_home")}
        </Link>
        <div className="flex items-center gap-2 font-serif text-lg">
          <Newspaper className="w-5 h-5 text-amber-300" /> {t("press.header")}
        </div>
        <SoundToggle />
        <LanguageSwitcher />
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 sm:px-10 pb-40 space-y-10" data-testid="press-content">
        <section>
          <div className="text-xs uppercase tracking-[0.3em] text-amber-300/90 font-bold">{t("press.kicker")}</div>
          <h1 className="mt-1 font-serif text-4xl sm:text-5xl font-black text-stone-50">Chronos Time Puzzle</h1>
          <p className="mt-3 text-stone-300 max-w-3xl leading-relaxed">{t("press.intro")}</p>
        </section>

        <section data-testid="press-facts">
          <h2 className="font-serif text-2xl font-bold text-stone-50 mb-4">{t("press.facts_h")}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {FACT_ITEMS(t).map((f) => (
              <div key={f.k} className="glass rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">{t(`press.fact_${f.k}_l`)}</div>
                <div className="mt-1 font-sans text-sm font-bold text-stone-100">{f.v}</div>
              </div>
            ))}
          </div>
        </section>

        <section data-testid="press-descriptions" className="space-y-4">
          <h2 className="font-serif text-2xl font-bold text-stone-50">{t("press.descriptions_h")}</h2>
          <CopyBox label={t("press.tagline_l")} text={t("press.tagline_v")} />
          <CopyBox label={t("press.short_l")} text={pressReleaseShort} />
          <CopyBox label={t("press.long_l")} text={pressReleaseLong} />
        </section>

        <section data-testid="press-trailer" className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Film className="w-6 h-6 text-fuchsia-300" />
            <h2 className="font-serif text-2xl font-bold text-stone-50">{t("press.trailer_h")}</h2>
            <span className="text-xs glass rounded-full px-3 py-1 text-fuchsia-200 border-fuchsia-400/40">30s</span>
          </div>
          <CopyBox label={t("press.trailer_l")} text={trailerScript} />
        </section>

        <section data-testid="press-assets">
          <h2 className="font-serif text-2xl font-bold text-stone-50 mb-4">{t("press.assets_h")}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <a
              href="/logo512.svg"
              download="chronos-time-puzzle-logo-512.svg"
              className="glass rounded-2xl p-5 flex items-center gap-4 hover:border-amber-400/50 transition-colors"
              data-testid="dl-logo"
            >
              <img src="/logo512.svg" alt="Logo Chronos" className="w-16 h-16 rounded-xl object-cover bg-stone-950" />
              <div className="flex-1">
                <div className="font-serif text-lg font-bold text-stone-100">{t("press.asset_logo")}</div>
                <div className="text-xs text-stone-400 mt-1">SVG · 512×512</div>
              </div>
              <Download className="w-5 h-5 text-amber-300" />
            </a>
            <a
              href="/logo192.svg"
              download="chronos-time-puzzle-icon-192.svg"
              className="glass rounded-2xl p-5 flex items-center gap-4 hover:border-amber-400/50 transition-colors"
              data-testid="dl-icon"
            >
              <img src="/logo192.svg" alt="Icône Chronos" className="w-16 h-16 rounded-xl object-cover bg-stone-950" />
              <div className="flex-1">
                <div className="font-serif text-lg font-bold text-stone-100">{t("press.asset_icon")}</div>
                <div className="text-xs text-stone-400 mt-1">SVG · 192×192</div>
              </div>
              <Download className="w-5 h-5 text-amber-300" />
            </a>
          </div>
          <p className="text-xs text-stone-300 mt-3">{t("press.assets_note")}</p>
        </section>

        <section data-testid="press-screenshots">
          <h2 className="font-serif text-2xl font-bold text-stone-50 mb-4">{t("press.shots_h")}</h2>
          <p className="text-sm text-stone-300 mb-4 max-w-3xl">{t("press.shots_intro")}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { i: 1, k: "hero", accent: "from-amber-500/30 to-rose-500/10", emoji: "🌟" },
              { i: 2, k: "map", accent: "from-emerald-500/30 to-teal-500/10", emoji: "🗺️" },
              { i: 3, k: "play", accent: "from-fuchsia-500/30 to-rose-500/10", emoji: "⏳" },
              { i: 4, k: "grimoire", accent: "from-teal-500/30 to-emerald-500/10", emoji: "📖" },
              { i: 5, k: "family", accent: "from-orange-500/30 to-amber-500/10", emoji: "👨‍👩‍👧" },
              { i: 6, k: "parental", accent: "from-stone-500/30 to-emerald-500/10", emoji: "🛡️" },
            ].map((s) => (
              <div key={s.i} className={`relative glass rounded-2xl p-5 aspect-[9/16] overflow-hidden`} data-testid={`shot-${s.k}`}>
                <div className={`absolute inset-0 bg-gradient-to-br ${s.accent}`} aria-hidden />
                <div className="relative z-10 flex flex-col h-full">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-bold">Shot #{s.i}</div>
                  <div className="mt-1 font-serif text-lg font-bold text-stone-50">{t(`press.shot_${s.k}_t`)}</div>
                  <p className="mt-2 text-xs text-stone-300 leading-relaxed">{t(`press.shot_${s.k}_d`)}</p>
                  <div className="mt-auto text-5xl opacity-80">{s.emoji}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section data-testid="press-contact" className="glass-strong rounded-3xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <Mail className="w-5 h-5 text-emerald-300" />
            <h2 className="font-serif text-2xl font-bold text-stone-50">{t("press.contact_h")}</h2>
          </div>
          <p className="text-sm text-stone-300 mb-3">{t("press.contact_desc")}</p>
          <div className="flex flex-wrap gap-3">
            <a href="mailto:press@chronostimepuzzle.com">
              <Button className="bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold rounded-full px-6" data-testid="press-email">
                press@chronostimepuzzle.com
              </Button>
            </a>
            <Link to="/">
              <Button variant="outline" className="border-stone-500/50 bg-transparent text-stone-200 hover:bg-stone-800 rounded-full px-6">
                {t("common.back_home")}
              </Button>
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
